import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { useCategories } from "@/hooks/useCategories";
import { CategoryIcon } from "@/components/CategoryIcon";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import { toast } from "sonner";
import { Loader2, Navigation } from "lucide-react";

export function ProOnboarding() {
  const navigate = useNavigate();
  const [selected, setSelected] = useState([]);
  const [specialty, setSpecialty] = useState("");
  const [bio, setBio] = useState("");
  const [city, setCity] = useState("");
  const [radius, setRadius] = useState([15]);
  const [coords, setCoords] = useState(null);
  const [locating, setLocating] = useState(false);
  const [loading, setLoading] = useState(false);

  const cats = useCategories();

  const toggle = (key) => setSelected((s) => s.includes(key) ? s.filter((k) => k !== key) : [...s, key]);

  const locate = () => {
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => { setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude }); setLocating(false); toast.success("Posizione acquisita"); },
      () => { setLocating(false); toast.error("Impossibile ottenere la posizione"); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!selected.length) { toast.error("Seleziona almeno una categoria: serve per abbinarti alle richieste"); return; }
    if (!coords) { toast.error("Acquisisci la tua posizione con il pulsante GPS"); return; }
    setLoading(true);
    try {
      const bioText = specialty.trim() ? `Specialità: ${specialty.trim()}${bio.trim() ? "\n" + bio.trim() : ""}` : bio;
      await api.post("/pro/onboarding", {
        categories: selected,
        bio: bioText,
        city,
        lat: coords.lat,
        lng: coords.lng,
        radius_km: radius[0],
      });
      toast.success("Profilo professionista creato!");
      navigate("/pro", { replace: true });
    } catch (err) { toast.error(errMsg(err)); } finally { setLoading(false); }
  };

  return (
    <div className="px-4 sm:px-6 pt-6 sm:pt-10 space-y-6 sm:max-w-xl sm:mx-auto" data-testid="pro-onboarding-page">
      <div className="space-y-1">
        <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Profilo professionista</h1>
        <p className="text-sm text-slate-600">Configura le tue categorie e la zona in cui operi.</p>
      </div>

      <form onSubmit={submit} className="space-y-6" data-testid="pro-onboarding-form">
        <div className="space-y-2">
          <Label>Le tue categorie</Label>
          <div className="flex flex-wrap gap-2" data-testid="onboarding-categories">
            {cats.map((c) => {
              const key = c.key || c.id;
              const active = selected.includes(key);
              return (
                <button key={key} type="button" data-testid={`onboarding-cat-${key}`} onClick={() => toggle(key)}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-semibold border transition-colors active:scale-95 ${
                    active ? "bg-whoo text-white border-whoo" : "bg-white text-slate-700 border-slate-200"
                  }`}>
                  <CategoryIcon icon={c.icon} className="w-4 h-4" /> {c.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="specialty">Mestiere o specialità <span className="text-slate-400 font-normal">(se non in lista)</span></Label>
          <Input id="specialty" data-testid="onboarding-specialty-input" value={specialty} onChange={(e) => setSpecialty(e.target.value)}
            className="h-14 bg-slate-50 border-transparent focus:bg-white focus:border-volt rounded-2xl"
            placeholder="Es. Imbianchino, badante, baby sitter..." />
          <p className="text-xs text-slate-500 leading-relaxed">Opzionale: se hai una specialità specifica (es. "riparazione stufe a pellet"), scrivila qui: sarà visibile nel tuo profilo. La categoria sopra resta necessaria per ricevere le richieste giuste.</p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="bio">Presentazione</Label>
          <Textarea id="bio" data-testid="onboarding-bio-input" rows={3} value={bio} onChange={(e) => setBio(e.target.value)}
            className="bg-slate-50 border-transparent focus:bg-white focus:border-volt rounded-2xl resize-none"
            placeholder="Es. Idraulico con 15 anni di esperienza, interventi rapidi in tutta Milano" />
        </div>

        <div className="space-y-2">
          <Label htmlFor="city">Città</Label>
          <Input id="city" data-testid="onboarding-city-input" value={city} onChange={(e) => setCity(e.target.value)}
            className="h-14 bg-slate-50 border-transparent focus:bg-white focus:border-volt rounded-2xl" placeholder="Es. Milano" />
        </div>

        <div className="space-y-2">
          <Label>Base operativa (GPS)</Label>
          <Button type="button" data-testid="onboarding-geolocate-btn" variant="outline" onClick={locate} disabled={locating}
            className={`w-full h-14 rounded-2xl font-semibold ${coords ? "border-emerald-300 text-emerald-600" : ""}`}>
            {locating ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Navigation className="w-5 h-5 mr-2" />{coords ? "Posizione acquisita" : "Usa la mia posizione"}</>}
          </Button>
        </div>

        <div className="space-y-3">
          <Label>Raggio di intervento: <span className="font-bold text-whoo">{radius[0]} km</span></Label>
          <Slider data-testid="onboarding-radius-slider" value={radius} onValueChange={setRadius} min={1} max={100} step={1} />
        </div>

        <Button data-testid="onboarding-submit-btn" type="submit" disabled={loading}
          className="w-full h-14 rounded-full bg-whoo hover:bg-whoo-dark text-white font-heading font-semibold text-base active:scale-[0.98] transition-transform">
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Salva e continua"}
        </Button>
      </form>
    </div>
  );
}
